import { useEffect } from 'react';
import Column from '@sdk/components/ui/spacing/Column';
import Row from '@sdk/components/ui/spacing/Row';
import Text11 from '@sdk/components/ui/typography/Text11';
import Text14 from '@sdk/components/ui/typography/Text14';
import gettext from '@sdk/lang/gettext';
import useAuth from '@sdk/authentication/useAuth';
import useConfig from '@sdk/config/useConfig';
import HorizontalSeparator from '@sdk/components/ui/HorizontalSeparator';
import EditablePicker from '@sdk/components/forms/EditablePicker';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import { AutomatedBreaksApiFields } from '@sdk/authentication/types/apiTypes';
import { Feature } from '@sdk/common/features';

import { TabType } from '@web/settings/types';
import { useSettingsErrorsContext } from '@web/settings/SettingsErrorContext';
import { useAutomatedBreaksSettings } from '@web/settings/components/schedule/Breaks/breaks.hooks';
import useBreaksForm from '@web/settings/components/schedule/Breaks/useBreaksForm';
import StandardBreaksContent from '@web/settings/components/schedule/Breaks/StandardBreaksContent/StandardBreaksContent';
import AutomatedBreaksContent from '@web/settings/components/schedule/Breaks/AutomatedBreaksContent/AutomatedBreaksContent';
import DisabledBreakAutomationOption from '@web/settings/components/schedule/Breaks/BreaksContent/DisabledBreakAutomationOption';
import { useBreakAutomationOptions } from '@web/settings/components/schedule/Breaks/BreaksContent/breaksContent.hooks';
import { FIELD_WIDTH_205 } from '@web/settings/constants';
import { getNumberFromBoolean } from '@web/settings/helpers/utils';
import { BrakeAutomationOptionValue } from '@web/settings/components/schedule/Breaks/breaks.types';

const BreaksContent = () => {
  const { currentUser, isOrgFeatureEnabled } = useAuth();
  const { automatedBreaks, updateAutomatedBreaksSettings } = useAutomatedBreaksSettings();
  const { fields } = useBreaksForm(automatedBreaks);
  const { breakAutomationOptions } = useBreakAutomationOptions();
  const { toggleError } = useSettingsErrorsContext();
  const {
    FEATURE_FLAGS: { AUTOMATED_BREAKS_ATTESTATIONS },
  } = useConfig();

  const areAutomatedBreaksEnabled = fields.isEnabled.value;
  const hasError =
    areAutomatedBreaksEnabled && !fields.meal.isEnabled.value && !fields.rest.isEnabled.value;

  useEffect(() => {
    toggleError(TabType.schedule, AutomatedBreaksApiFields.automatedBreaksIsEnabled, hasError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields.isEnabled, fields.meal.isEnabled, fields.rest.isEnabled]);

  const optionsRenderer = ({ label, value }) => {
    if (!!value && !currentUser.org.isPremium) {
      return <DisabledBreakAutomationOption label={label} />;
    }
    return label;
  };

  const onChange = (breakAutomation: BrakeAutomationOptionValue) => {
    fields.isEnabled.onChange(!!breakAutomation);

    const fieldsToUpdate = {
      [AutomatedBreaksApiFields.automatedBreaksIsEnabled]: !!breakAutomation,
    };

    if (
      breakAutomation &&
      AUTOMATED_BREAKS_ATTESTATIONS &&
      isOrgFeatureEnabled(Feature.breaksAttestation)
    ) {
      // once somebody enables automated breaks then also enable attestations for them by default
      // which they can toggle off
      fields.isAttestation.onChange(true);
      fieldsToUpdate[AutomatedBreaksApiFields.automatedBreaksIsAttestation] = true;
    }

    updateAutomatedBreaksSettings(fieldsToUpdate);
  };

  return (
    <Column>
      <Row mTop={20} mBottom={20}>
        <HorizontalSeparator />
      </Row>
      <Row alignItems="center" justifyContent="space-between">
        <Column>
          <Text14 semiBold themeColor={ThemeColorType.gray}>
            {gettext('Break type')}
          </Text14>
          <Text14 themeColor={ThemeColorType.gray}>
            {gettext('Specify whether breaks are standard or automated based on rules.')}
          </Text14>
        </Column>

        <EditablePicker
          disabled={!currentUser.isAdmin}
          options={breakAutomationOptions}
          value={getNumberFromBoolean(fields.isEnabled.value)}
          customWidth={FIELD_WIDTH_205}
          onChange={onChange}
          renderItem={optionsRenderer}
          invalid={hasError}
        />
      </Row>
      {hasError && (
        <Row mTop={10}>
          <Text11 themeColor={ThemeColorType.danger} bold>
            {gettext('Change the break type or enable breaks for automation')}
          </Text11>
        </Row>
      )}

      {!areAutomatedBreaksEnabled && <StandardBreaksContent />}
      {areAutomatedBreaksEnabled && <AutomatedBreaksContent fields={fields} />}
    </Column>
  );
};

export default BreaksContent;
